import CoreBase from '../../services/engines/CoreBase';
import { offsetToMs } from './ReminderOffset';
import moment from 'moment-timezone';
import userSettingsEngine from '../../controllers/UserSettings/UserSettingsEngine';

// Fallback layout — bottom area, left of the UpcomingDrawer (210px width + margin
// in upcomingDrawerSx.js). Card width ~148px, footer height ~24px.
const DRAWER_WIDTH = 210;
const MARGIN = 8;

/**
 * CalEventObj — data container for a CalEvent.
 * Constructed from a backend object — straight field assignment, no defaults,
 * except pos (see below).
 * Caller (CalendarEngine.onCalEventsReceived/onCalEventUpdated) guarantees
 * that event is not null/undefined.
 * Contains pure computations based on ems — no engine or React dependencies.
 */
export default class CalEventObj extends CoreBase {
    /**
     * Creates a CalEventObj from a raw backend event object.
     * posX/posY → pos is derived here via _derivePos() — the single place
     * this happens, for both the CalendarFetcher REST fetch and the SignalR
     * push (onCalEventUpdated). setPos() uses the same function after a
     * drag-stop — pos can therefore never drift from posX/posY.
     * @param {object} event - raw CalEvent object from backend
     */
    constructor(event) {
        super();

        this.rk = event.rk;
        this.label = event.label;
        this.desc = event.desc;
        this.date = event.date;
        this.time = event.time;
        this.cat = event.cat;
        this.remind = event.remind;
        this.ems = event.ems;
        this.createdOn = event.createdOn;
        this.stateId = event.stateId;
        this.app = event.app;
        // Persisted position from BE — 0 means "not set"
        this.posX = event.posX;
        this.posY = event.posY;
        // FE-only position of the floating card — { top, left }, always derived
        // from posX/posY via _derivePos(), never written directly.
        this.pos = _derivePos(this.posX, this.posY);
    }

    /**
     * True if the event has a real, persisted position (posX/posY > 0),
     * as opposed to the fallback position. 0 means "not set" on the
     * backend entity.
     * @returns {boolean}
     */
    hasRealPos() {
        return this.posX > 0 && this.posY > 0;
    }

    /**
     * Persists a new position — posX/posY and pos in 1 step, via the same
     * _derivePos() the constructor uses. The only way to set a real
     * (drag-stop) position — CalendarUpdater.updatePosition calls this
     * before the BE persist starts.
     * @param {{ top: number, left: number }} pos
     */
    setPos(pos) {
        this.posX = pos.left;
        this.posY = pos.top;
        this.pos = _derivePos(this.posX, this.posY);
    }

    /**
     * Persists a new date/time — date/time and ems in 1 step, via the same
     * _deriveEms() everywhere. The only way to change date/time —
     * CalendarUpdater.updateEvent calls this before the BE persist starts,
     * so ems can never drift from date/time (same principle as
     * setPos()/_derivePos() above).
     * @param {string} date - YYYY-MM-DD
     * @param {string} time - HH:MM
     */
    setDateTime(date, time) {
        this.date = date;
        this.time = time;
        this.ems = _deriveEms(date, time);
    }

    /**
     * Returns true if the event lies in the past.
     * @returns {boolean}
     */
    isOverDue() {
        if (!this.ems) return false;
        return this.ems < Date.now();
    }

    /**
     * Returns true if the card should "ring" — event is overdue, or the
     * reminder time (ems - offset) has passed. Computed, no stored state —
     * stays correct after a page refresh.
     * @returns {boolean}
     */
    isRinging() {
        if (!this.ems) return false;
        if (this.isOverDue()) return true;

        const offsetMs = offsetToMs(this.remind);
        if (offsetMs === 0) return false;

        return Date.now() >= this.ems - offsetMs;
    }

    /**
     * Milliseconds until the event. Negative if overdue.
     * @returns {number}
     */
    msUntil() {
        if (!this.ems) return 0;
        return this.ems - Date.now();
    }
}

// ─── helpers ──────────────────────────────────────────

/**
 * Derives { top, left } from posX/posY — real position if both > 0,
 * otherwise the default fallback. The single place this derivation
 * happens — constructor and setPos() both call it, no one else ever
 * writes directly to .pos.
 * @param {number} posX
 * @param {number} posY
 * @returns {{ top: number, left: number }}
 */
function _derivePos(posX, posY) {
    return posX > 0 && posY > 0 ? { top: posY, left: posX } : _defaultPos();
}

/**
 * Returns the default fallback position for an event without a real
 * posX/posY — bottom area, left of the UpcomingDrawer. CalendarEngine.
 * _freeFallbackPos() refines this at show()-time with a stacking offset
 * against other currently visible fallback events — that needs live
 * cache state and can't happen here.
 * @returns {{ top: number, left: number }}
 */
function _defaultPos() {
    return {
        top: window.innerHeight - 220,
        left: window.innerWidth - 168 - DRAWER_WIDTH - MARGIN,
    };
}

/**
 * Derives ems (epoch ms) from date + time — parsed in the user's
 * configured timeZone setting (userSettingsEngine.resolve('timeZone'),
 * IANA zone, default Europe/Amsterdam), not browser/OS-local. Symmetric
 * with CalEventFactory.defaultDateTime(), which converts ems back to
 * date/time strings in the same zone. The single place this derivation
 * happens — setDateTime() calls it, no one else ever writes directly
 * to .ems.
 * @param {string} date - YYYY-MM-DD
 * @param {string} time - HH:MM
 * @returns {number} epoch ms, or 0 if date/time is missing
 */
function _deriveEms(date, time) {
    if (!date || !time) return 0;
    const tz = userSettingsEngine.resolve('timeZone');
    return moment.tz(`${date}T${time}`, tz).valueOf();
}
